import { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { DiscordContext } from './DiscordContext';


/**
 * Land the embedded Discord client on the right surface once the boot
 * fetch settles:
 *
 *   - If a channel lobby exists whose platform binding's `instance_id`
 *     matches THIS Activity instance, deep-link to `/game/:lobbyUuid`.
 *     Discord's "Join Activity" launches every participant with the
 *     same `instanceId`, so the owner's lobby is the one whose binding
 *     carries that id. Click their face → click Join → land in their
 *     game. (No matter who is the owner — the joiner lands directly.)
 *
 *   - Otherwise fall back to `/` (Main Menu). This covers the
 *     owner-on-first-boot case (no lobby exists yet) and the
 *     fresh-Activity case.
 *
 * Historical context — CC_DISCORD_MAIN_MENU retired (2026-05-30): the
 * boot fetch became a read-only GET that seats no one, so the old
 * owner-vs-joiner branch that force-pushed joiners to `/casual` is
 * gone. It read `lobbyIsOwner` / `lobbySeatIndex` (both now null on
 * boot). After CC_FE_PAGES (2026-06-01) moved the channel-lobby tiles
 * off Main Menu onto `/lobbies`, sending every Discord user to `/`
 * stranded joiners with no Join affordance — fixed here by deep-linking
 * directly to the matching `/game/:lobbyUuid`. Handoff §5
 * (DISCORD_KNOWLEDGE.md §6.1 revisit) closed.
 *
 * The navigator must fire exactly once per mount — on the first time
 * it sees `channelLobbiesFetched === true`.
 *
 * The flag is NOT a reliable one-shot signal. A menu Join POSTs
 * from_platform, which (after CC_DISCORD_LOBBY_LIFECYCLE's §5 push)
 * re-fetches the channel lobbies and re-flips `channelLobbiesFetched`
 * false→true→… ; any such churn would re-run this effect and yank the
 * user back to `/` mid-navigation — the exact "Join goes to main menu"
 * prime-directive violation (bug 4). A `useRef` latch guards against
 * that: the redirect fires on the first qualifying render and never
 * again for the life of this mount.
 *
 * The deps array intentionally omits `navigate` because
 * react-router-dom 6.3's `useNavigate` returns a fresh function
 * reference on every render (stabilized in 6.4). Including it in deps
 * would refire this effect on every router-context update — yanking the
 * user back to `/` mid-navigation. When react-router-dom is upgraded to
 * >= 6.4, navigate can be added back to the dep array safely.
 *
 * `channelLobbies` and `instanceId` are read inside the effect but not
 * in deps: the effect only runs when `channelLobbiesFetched` flips
 * true, at which point both are populated; subsequent churn is locked
 * out by the latch.
 *
 * Lives inside the Router tree so `useNavigate` resolves; renders
 * nothing. Re-exported from App.js so tests can render it in isolation
 * without dragging the full <App> import graph through jest's module
 * resolver.
 */
export function DiscordLobbyNavigator() {
    const discord = useContext(DiscordContext);
    const navigate = useNavigate();
    const isEmbedded = discord.isEmbedded;
    const channelLobbiesFetched = discord.channelLobbiesFetched;
    const channelLobbies = discord.channelLobbies;
    const instanceId = discord.instanceId;
    // Latch: the boot redirect must fire exactly once per mount.
    // `channelLobbiesFetched` re-flips when a menu Join re-fetches the
    // channel lobbies (CC_DISCORD_LOBBY_LIFECYCLE §6.1), so without this
    // guard the effect would re-run on that churn and yank the user back
    // to '/' after they joined — the bug-4 prime-directive violation.
    const didFireRef = useRef(false);
    // Deps [isEmbedded, channelLobbiesFetched]. navigate is omitted on
    // purpose: react-router-dom 6.3's useNavigate is a fresh ref every
    // render, so including it would refire this effect and yank the user
    // back to '/' mid-navigation. channelLobbies / instanceId are read
    // inside but stay out of deps for the same single-fire reason; the
    // latch locks later churn out.
    useEffect(() => {
        if (!isEmbedded) return;
        if (!channelLobbiesFetched) return;
        if (didFireRef.current) return;
        didFireRef.current = true;

        // Deep-link to the lobby bound to this Activity instance, if any.
        let targetUuid = null;
        if (instanceId && Array.isArray(channelLobbies)) {
            for (const entry of channelLobbies) {
                const lobby = entry && entry.lobby;
                const bindings = (lobby && lobby.platform_bindings) || [];
                const hit = bindings.find((b) => b && b.discord_meta
                    && b.discord_meta.instance_id === instanceId);
                if (hit && lobby && lobby.uuid) {
                    targetUuid = lobby.uuid;
                    break;
                }
            }
        }

        if (targetUuid) {
            console.info(
                '[DiscordSDK] navigator → /game/%s (instance match)',
                targetUuid);
            navigate('/game/' + targetUuid, { replace: true });
            return;
        }
        console.info('[DiscordSDK] navigator → / (main menu)');
        navigate('/', { replace: true });
    }, [isEmbedded, channelLobbiesFetched]);
    return null;
}
